import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { ImpactByMaterialJadlog } from "@/hooks/useImpactDataJadlog";
import { IMPACT_METRIC_COLORS_JADLOG } from "@/utils/impactMetricColorsJadlog";

interface ImpactByMaterialChartJadlogProps {
  data: ImpactByMaterialJadlog[];
}

const CHART_COLORS_JADLOG = {
  co2: IMPACT_METRIC_COLORS_JADLOG.co2,
  energy: IMPACT_METRIC_COLORS_JADLOG.energy,
  recovered: IMPACT_METRIC_COLORS_JADLOG.recovered,
};

// Materiais que não possuem regra de impacto definida
const MATERIALS_WITHOUT_IMPACT_RULE = ["Madeira", "Sem Regra"];

export const ImpactByMaterialChartJadlog = ({
  data,
}: ImpactByMaterialChartJadlogProps) => {
  // Filtrar apenas materiais com regra de impacto
  const filteredData = data.filter(
    (item) => !MATERIALS_WITHOUT_IMPACT_RULE.includes(item.material),
  );

  const formatYAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString("pt-BR")}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center gap-6 mt-3">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="bg-card shadow-md hover:shadow-lg transition-shadow">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-[#10261F]">
          Impacto por tipo de resíduo
        </CardTitle>
        <CardDescription className="text-gray-600">
          Contribuição de cada resíduo no impacto ambiental total
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                vertical={false}
              />
              <XAxis
                dataKey="material"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={formatYAxis}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              <Bar
                dataKey="co2"
                name="CO₂ evitado"
                fill={CHART_COLORS_JADLOG.co2}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="energy"
                name="Energia gerada (kWh)"
                fill={CHART_COLORS_JADLOG.energy}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="recovered"
                name="Material recuperado"
                fill={CHART_COLORS_JADLOG.recovered}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
